import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, X, Edit2, AlertCircle, ChevronDown, ChevronRight, TrendingDown } from 'lucide-react';
import { reorderAPI, plannerAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import Select from '../Select';
import DatePicker from '../DatePicker';
import SortHeader, { useSort, sortRows } from '../SortHeader';
import TablePager, { usePagedRows } from '../TablePager';
import useDialog from '../../hooks/useDialog';
import { formatClinicDate } from '../../utils/clinicTime';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import { isOpeningStock } from './OrderBoardView';

// What a blank setting means until the clinic's planner defaults have loaded. Cover is set per
// spend class (A, B, C) in those defaults; an item's own setting always wins.
export const REORDER_DEFAULTS = { coverDays: 30, safetyDays: 5, minPacks: 1 };
const FALLBACK_DEFAULTS = { coverA: 30, coverB: 30, coverC: 30, safetyDays: 5 };
const coverDefault = (defaults, r) => defaults[`cover${r.abcClass || 'C'}`] ?? REORDER_DEFAULTS.coverDays;
const CLASS_CHIP = {
  A: 'bg-red-50 text-red-800 border-red-200',
  B: 'bg-amber-50 text-amber-800 border-amber-200',
  C: 'bg-gray-50 text-gray-700 border-gray-200',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'medicine', label: 'Medicines' },
  { id: 'supply', label: 'Supplies' },
  { id: 'custom', label: 'Changed from default' },
  { id: 'seeded', label: 'Has a starting rate' },
  { id: 'unseeded', label: 'No starting rate' },
  { id: 'excluded', label: 'Left off orders' },
];

const DEMAND_KIND_LABEL = {
  SHORTFALL: 'Cut at the counter',
  REFUSED_AT_PRESCRIBING: 'Refused in the writer',
  OUTSIDE: 'Written to buy outside',
};

const today = () => new Date().toISOString().slice(0, 10);
const isCustomised = (r) =>
  r.preferredVendorId || r.coverDays != null || r.safetyDays != null || r.minPacks != null || r.excludeUntil;
const isExcluded = (r) => !!r.excludeUntil && r.excludeUntil > today();
const isLeftOff = (r) => isExcluded(r) || !!r.snoozeUntil;
const fmtRate = (v) => (v == null ? '' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

/**
 * Item Settings on Medicine Orders: how each stocked item is reordered where it differs from the
 * defaults, and the usage rate it starts from. The purchase planner reads all of it; a person only
 * ever needs to touch the exceptions.
 */
const ReorderSettingsView = ({ vendors, canEdit }) => {
  const notify = useToast();
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [showDemand, setShowDemand] = useState(false);
  const [defaults, setDefaults] = useState(FALLBACK_DEFAULTS);
  const { sortConfig, handleSort } = useSort('name');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settings, demand, d] = await Promise.all([
        reorderAPI.getSettings(),
        reorderAPI.getDemandEvents().catch(() => []),
        plannerAPI.getDefaults().catch(() => null),
      ]);
      setRows(Array.isArray(settings) ? settings : []);
      setEvents(Array.isArray(demand) ? demand : []);
      if (d) setDefaults({ ...FALLBACK_DEFAULTS, ...d });
    } catch (e) {
      setError(e.message || 'Could not load item settings');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Counts and the filtered list from one pass, so a chip's number is always what clicking it shows.
  const { counts, visible } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = {
      all: () => true,
      medicine: (r) => r.category !== 'SUPPLY',
      supply: (r) => r.category === 'SUPPLY',
      custom: (r) => !!isCustomised(r),
      seeded: (r) => r.seedRatePerDay != null,
      unseeded: (r) => r.seedRatePerDay == null,
      excluded: (r) => isLeftOff(r),
    };
    const searched = q
      ? rows.filter(r => `${r.name} ${r.medicineCode || ''}`.toLowerCase().includes(q))
      : rows;
    const c = {};
    FILTERS.forEach(f => { c[f.id] = searched.filter(matches[f.id]).length; });
    return { counts: c, visible: searched.filter(matches[filter]) };
  }, [rows, search, filter]);

  const sorted = useMemo(() => sortRows(visible, sortConfig, {
    name: (r) => r.name,
    stock: (r) => Number(r.quantity || 0),
    supplier: (r) => r.preferredVendorName || r.lastVendorName || '',
    rate: (r) => (r.seedRatePerDay == null ? null : Number(r.seedRatePerDay)),
    cls: (r) => r.abcClass || 'C',
    cover: (r) => r.coverDays ?? coverDefault(defaults, r),
    safety: (r) => r.safetyDays ?? defaults.safetyDays,
    minPacks: (r) => r.minPacks ?? REORDER_DEFAULTS.minPacks,
    excluded: (r) => (isExcluded(r) ? r.excludeUntil : r.snoozeUntil || null),
  }), [visible, sortConfig, defaults]);

  // Missed demand per item over the last 30 days, for the panel and the row marker.
  const missedByItem = useMemo(() => {
    const m = new Map();
    events.forEach(e => {
      const cur = m.get(e.medicineId) || { name: e.medicineName, count: 0, units: 0 };
      cur.count += 1;
      cur.units += e.units || 0;
      m.set(e.medicineId, cur);
    });
    return m;
  }, [events]);

  const paging = usePagedRows(sorted, { id: 'reorder-items', resetOn: [search, filter, sortConfig] });
  const eventsPaging = usePagedRows(events, { id: 'reorder-missed-demand' });

  const onSaved = (saved) => {
    setRows(prev => prev.map(r => (r.medicineId === saved.medicineId ? saved : r)));
    setEditing(null);
    notify('success', `${saved.name} saved`);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" /><span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Missed demand: what the shelf could not supply. The planner adds it to what was sold, since
          an item that keeps running out otherwise looks slow exactly when it is most wanted. */}
      <div className="bg-white rounded-2xl shadow-xl">
        <button
          type="button"
          onClick={() => setShowDemand(v => !v)}
          aria-expanded={showDemand}
          className="w-full flex items-center justify-between p-5 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <span className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><TrendingDown className="h-5 w-5" /></span>
            <span>
              <span className="block font-bold text-gray-800">Missed demand, last 30 days</span>
              <span className="block text-sm text-gray-500">
                {events.length === 0
                  ? 'Nothing has been cut at the counter or refused in the writer for want of stock.'
                  : `${events.length} time(s), across ${missedByItem.size} item(s)`}
              </span>
            </span>
          </span>
          {showDemand ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
        </button>
        {showDemand && events.length > 0 && (
          <div className="border-t border-gray-100">
          <TablePager paging={eventsPaging} noun="entry" nounPlural="entries" className="px-4" rounded={false} />
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-32" /><col /><col className="w-48" /><col className="w-24" /><col className="w-44" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">What happened</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Units</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Recorded by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {eventsPaging.pageRows.map(e => (
                  <tr key={e.id}>
                    <td className="px-4 py-2 text-gray-600">{formatClinicDate(e.eventDate, { day: '2-digit', month: 'short' })}</td>
                    <td className="px-4 py-2 font-medium text-gray-800 truncate">{e.medicineName}</td>
                    <td className="px-4 py-2 text-gray-600">{DEMAND_KIND_LABEL[e.kind] || e.kind}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{e.units ?? ''}</td>
                    <td className="px-4 py-2 text-gray-500 truncate">{e.recordedBy || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePager paging={eventsPaging} noun="entry" nounPlural="entries" position="bottom" className="px-4" />
          </div>
        )}
      </div>

      <DefaultsCard defaults={defaults} canEdit={canEdit} onSaved={setDefaults} />

      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-5 border-b border-gray-100 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[14rem]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or code"
                className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm"
              />
              {search && (
                <button type="button" aria-label="Clear search" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Grey figures are the defaults below. Class A is the costliest 70% of what the clinic uses, B the next 20%, C the rest.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={filter === f.id}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {f.label} <span className={filter === f.id ? 'text-white/80' : 'text-gray-500'}>{counts[f.id]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Square corners: the bar sits under the search and filters, not at the card's top edge. */}
        <TablePager paging={paging} noun="item" filteredFrom={rows.length} className="px-4" rounded={false} />
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[24%]" /><col className="w-20" /><col className="w-[17%]" /><col className="w-24" /><col className="w-20" />
              <col className="w-20" /><col className="w-20" /><col className="w-24" /><col className="w-36" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <SortHeader label="Item" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Stock" sortKey="stock" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Supplier" sortKey="supplier" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Used / day" sortKey="rate" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Class" sortKey="cls" sortConfig={sortConfig} onSort={handleSort} align="center" />
                <SortHeader label="Cover" sortKey="cover" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Safety" sortKey="safety" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Min packs" sortKey="minPacks" sortConfig={sortConfig} onSort={handleSort} align="right" />
                <SortHeader label="Left off" sortKey="excluded" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">No items match.</td></tr>
              ) : paging.pageRows.map(r => {
                const open = () => canEdit && setEditing(r);
                const missed = missedByItem.get(r.medicineId);
                return (
                  <tr
                    key={r.medicineId}
                    tabIndex={canEdit ? 0 : undefined}
                    onClick={open}
                    onKeyDown={canEdit ? onActivateKey(open) : undefined}
                    className={`${canEdit ? 'cursor-pointer hover:bg-gray-50' : ''} ${ROW_FOCUS_RING}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 truncate" title={r.name}>{r.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {r.medicineCode}
                        {missed && <span className="ml-2 text-amber-700">missed {missed.count}x</span>}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{Number(r.quantity || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 truncate">
                      {r.preferredVendorName
                        ? <span className="font-medium text-gray-800" title="Preferred supplier">{r.preferredVendorName}</span>
                        : <span className="text-gray-500" title="Last delivered by">{r.lastVendorName || ''}</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums" title={r.seedSource || 'No starting rate yet'}>
                      {r.seedRatePerDay != null ? fmtRate(r.seedRatePerDay) : <span className="text-gray-400">none</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span title={r.pinnedClass ? 'Set by hand' : 'Worked out from what it costs to keep using'}
                        className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-semibold ${CLASS_CHIP[r.abcClass || 'C']}`}>
                        {r.abcClass || 'C'}{r.pinnedClass ? ' *' : ''}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${r.coverDays != null ? 'font-semibold text-navy-800' : 'text-gray-400'}`}>{r.coverDays ?? coverDefault(defaults, r)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${r.safetyDays != null ? 'font-semibold text-navy-800' : 'text-gray-400'}`}>{r.safetyDays ?? defaults.safetyDays}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${r.minPacks != null ? 'font-semibold text-navy-800' : 'text-gray-400'}`}>{r.minPacks ?? REORDER_DEFAULTS.minPacks}</td>
                    <td className="px-4 py-3 truncate" title={isExcluded(r) ? r.excludeReason : r.snoozeReason || undefined}>
                      {isExcluded(r)
                        ? <span className="text-amber-800">until {formatClinicDate(r.excludeUntil, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        : r.snoozeUntil
                          ? <span className="text-gray-600">for now, to {formatClinicDate(r.snoozeUntil, { day: '2-digit', month: 'short' })}</span>
                          : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager paging={paging} noun="item" filteredFrom={rows.length} position="bottom" className="px-4" />
      </div>

      {editing && (
        <ItemSettingsModal row={editing} vendors={vendors} defaults={defaults} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
};

const numOrNull = (v) => (v === '' || v == null ? null : parseInt(v, 10));

// The clinic's planner defaults: how many days an order covers for each spend class, and the
// safety buffer. Every item without its own setting follows these.
const DefaultsCard = ({ defaults, canEdit, onSaved }) => {
  const notify = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setForm(defaults); }, [defaults, editing]);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await plannerAPI.saveDefaults({
        coverA: numOrNull(form.coverA), coverB: numOrNull(form.coverB),
        coverC: numOrNull(form.coverC), safetyDays: numOrNull(form.safetyDays),
      });
      onSaved({ ...FALLBACK_DEFAULTS, ...saved });
      setEditing(false);
      notify('success', 'Planner defaults saved');
    } catch (e) {
      notify('error', e.message || 'Could not save the defaults');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label) => (
    <div key={key}>
      <p className="text-xs text-gray-500">{label}</p>
      {editing ? (
        <input type="number" inputMode="numeric" value={form[key] ?? ''} aria-label={label}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-20 p-1.5 border border-gray-200 rounded-lg text-right font-semibold" />
      ) : (
        <p className="text-lg font-bold text-gray-900">{defaults[key]} days</p>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl p-5 flex items-center justify-between gap-6 flex-wrap">
      <div>
        <p className="font-semibold text-gray-800">Planner defaults</p>
        <p className="text-xs text-gray-500">How long one order should last, by spend class, and the buffer kept for a late delivery.</p>
      </div>
      <div className="flex items-end gap-6 flex-wrap">
        {field('coverA', 'Cover, class A')}
        {field('coverB', 'Cover, class B')}
        {field('coverC', 'Cover, class C')}
        {field('safetyDays', 'Safety')}
        {canEdit && (editing ? (
          <div className="flex gap-2">
            <button type="button" onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg bg-gray-100 text-sm">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">Change</button>
        ))}
      </div>
    </div>
  );
};

const ItemSettingsModal = ({ row, vendors, defaults, onClose, onSaved }) => {
  const notify = useToast();
  const [preferredVendorId, setPreferredVendorId] = useState(row.preferredVendorId || '');
  const [coverDays, setCoverDays] = useState(row.coverDays ?? '');
  const [safetyDays, setSafetyDays] = useState(row.safetyDays ?? '');
  const [minPacks, setMinPacks] = useState(row.minPacks ?? '');
  const [excludeUntil, setExcludeUntil] = useState(row.excludeUntil || '');
  const [excludeReason, setExcludeReason] = useState(row.excludeReason || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [abcClass, setAbcClass] = useState(row.pinnedClass || '');
  const [clearSkip, setClearSkip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialog(onClose);

  const supplierOptions = useMemo(() => [
    { value: '', label: row.lastVendorName ? `Whoever delivered it last (${row.lastVendorName})` : 'Whoever delivered it last' },
    ...vendors
      .filter(v => v.status !== 'SUSPENDED' && (v.vendorType === 'PHARMACEUTICAL' || v.vendorType === 'LABORATORY') && !isOpeningStock(v))
      .slice()
      .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
      .map(v => ({ value: v.id, label: v.vendorName })),
  ], [vendors, row.lastVendorName]);

  const save = async () => {
    setError('');
    const cover = numOrNull(coverDays);
    const safety = numOrNull(safetyDays);
    const packs = numOrNull(minPacks);
    if (cover != null && (cover < 1 || cover > 365)) { setError('Cover is between 1 and 365 days.'); return; }
    if (safety != null && (safety < 0 || safety > 90)) { setError('Safety is between 0 and 90 days.'); return; }
    if (packs != null && packs < 1) { setError('Minimum packs is at least 1.'); return; }
    if (excludeUntil && !excludeReason.trim()) { setError('Say why it is being left off orders.'); return; }
    setSaving(true);
    try {
      const saved = await reorderAPI.saveSettings(row.medicineId, {
        preferredVendorId: preferredVendorId || null,
        coverDays: cover,
        safetyDays: safety,
        minPacks: packs,
        excludeUntil: excludeUntil || null,
        excludeReason: excludeUntil ? excludeReason.trim() : null,
        notes: notes.trim() || null,
        abcClass: abcClass || null,
        clearSkip,
      });
      onSaved(saved);
    } catch (e) {
      setError(e.message || 'Could not save');
      notify('error', e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const numberField = (label, value, set, placeholder, hint) => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        onChange={e => set(e.target.value)}
        placeholder={String(placeholder)}
        className="w-full p-2.5 border border-gray-200 rounded-lg"
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center"><Edit2 className="w-5 h-5 text-white" /></div>
            <div>
              <h2 className="text-xl font-bold text-white">{row.name}</h2>
              <p className="text-white/80 text-sm">How this item is reordered</p>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="w-5 h-5 text-white" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm">
            <p className="font-semibold text-gray-800">
              Starting rate: {row.seedRatePerDay != null ? `${fmtRate(row.seedRatePerDay)} a day` : 'none yet'}
            </p>
            <p className="text-gray-600 mt-0.5">
              {row.seedRatePerDay != null
                ? row.seedSource || 'From the old system\'s stock counts.'
                : 'Until the new system has a few weeks of its own sales, the planner has nothing to go on for this item.'}
            </p>
          </div>

          {row.snoozeUntil && !clearSkip && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm flex items-center justify-between gap-3">
              <span className="text-amber-900">
                Left off orders until {formatClinicDate(row.snoozeUntil, { day: 'numeric', month: 'short' })}{row.snoozeReason ? `: ${row.snoozeReason}` : ''}.
                {row.snoozeFloor > 0 && ` Back sooner if fewer than ${row.snoozeFloor} are in stock.`}
              </span>
              <button type="button" onClick={() => setClearSkip(true)} className="text-sm font-medium text-emerald-700 hover:underline whitespace-nowrap">Put it back</button>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold mb-1">Spend class</label>
            <Select
              value={abcClass}
              onChange={setAbcClass}
              options={[
                { value: '', label: `Worked out automatically (now ${row.abcClass || 'C'})` },
                { value: 'A', label: 'A: costly, kept tight, 95% safe' },
                { value: 'B', label: 'B: middle' },
                { value: 'C', label: 'C: cheap, kept generous' },
              ]}
              buttonClassName="p-2.5 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Order from</label>
            <Select value={preferredVendorId} onChange={setPreferredVendorId} options={supplierOptions} buttonClassName="p-2.5 rounded-lg" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {numberField('Cover (days)', coverDays, setCoverDays, coverDefault(defaults, { abcClass: abcClass || row.abcClass }), 'Days one order should last.')}
            {numberField('Safety (days)', safetyDays, setSafetyDays, defaults.safetyDays, 'Kept back for a late delivery.')}
            {numberField('Minimum packs', minPacks, setMinPacks, REORDER_DEFAULTS.minPacks, 'Never order fewer.')}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Leave off orders until</label>
              <DatePicker value={excludeUntil} onChange={setExcludeUntil} placeholder="Not left off" min={today()} />
              {excludeUntil && (
                <button type="button" onClick={() => { setExcludeUntil(''); setExcludeReason(''); }} className="mt-1 text-xs text-emerald-700 hover:underline">
                  Put it back on orders
                </button>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold mb-1">
                Why {excludeUntil && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={excludeReason}
                onChange={e => setExcludeReason(e.target.value)}
                disabled={!excludeUntil}
                maxLength={500}
                placeholder={excludeUntil ? 'e.g. Doctor has stopped prescribing it for now' : 'Choose a date first'}
                className="w-full p-2.5 border border-gray-200 rounded-lg disabled:bg-gray-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={500} className="w-full p-2.5 border border-gray-200 rounded-lg" />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
            </div>
          )}
        </div>

        <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl font-medium">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReorderSettingsView;
